import { useAuth0 } from "@auth0/auth0-react";
import { User } from "@models";
import {
  AccountTree,
  ArrowDropDown,
  Assignment,
  Campaign,
  Chat,
  Dashboard as DashboardIcon,
  Difference,
  MenuBook,
  Route,
  School,
  Widgets,
} from "@mui/icons-material";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import {
  Badge,
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { cn, fillRouteWithSlugs } from "@utils";
import { observer } from "mobx-react-lite";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { UserMenu } from "src/components/NavBar/userMenu/UserMenu";
import {
  getFirst2Initials,
  stringToColorHash,
} from "src/components/NavBar/utils";
import { ROUTE_SLUGS } from "src/config/navigation/routes";
import { useCrisp } from "src/hooks/Crisp";
import { useGetChangelogIndex } from "src/hooks/queries/changelog";
import { useStore } from "src/stores";
import { getOrgFromUser } from "src/utils/user";
import { ROUTES, theme } from "../../config";
import {
  useCurrentPath,
  useGetDevices,
  useGetUser,
  useGetUserTodosSummary,
  useGlobalDeviceContext,
} from "../../hooks";
import DeviceNavbar from "../DeviceNavbar/DeviceNavbar";
import { LeftDrawerSkeleton } from "./LeftDrawerSkeleton";
import { SidebarSectionHeader } from "./SidebarSectionHeader";
import { getLeftDrawerRootClassName } from "./leftDrawerShared";

const AGENT_CONTEXT_SEARCH_PARAMS = ["agentThreadId", "tiptapSuggestionId"];

const getRouteWithAgentContext = (route: string, currentSearch: string) => {
  const currentSearchParams = new URLSearchParams(currentSearch);
  const [routePath, routeSearch = ""] = route.split("?");
  const nextSearchParams = new URLSearchParams(routeSearch);

  AGENT_CONTEXT_SEARCH_PARAMS.forEach((key) => {
    const value = currentSearchParams.get(key);

    if (value) {
      nextSearchParams.set(key, value);
    }
  });

  const searchSuffix = nextSearchParams.toString();

  return searchSuffix ? `${routePath}?${searchSuffix}` : routePath;
};

export const LeftDrawer = observer(({ hasBanner }: { hasBanner: boolean }) => {
  const location = useLocation();
  const { pathname, search } = location;
  const { orgId = "" } = useParams();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isGeneralExpanded, setIsGeneralExpanded] = useState(true);
  const [isMoreExpanded, setIsMoreExpanded] = useState(true);
  const [anchorUserMenu, setAnchorUserMenu] = useState<HTMLElement | null>(
    null
  );

  const [anchorOrgMenu, setAnchorOrgMenu] = useState<Element | null>(null);
  const [isOrgMenuOpen, setIsOrgMenuOpen] = useState(false);

  const currentPath = useCurrentPath();
  const { isAuthenticated } = useAuth0();
  const isQualityEventsMenuEnabled = useFeatureFlagEnabled("qem-menu-item");
  const isChangeManagementMenuEnabled = useFeatureFlagEnabled(
    "change-management-menu-item"
  );
  const { uiStore } = useStore();

  const { data: user } = useGetUser(isAuthenticated);

  const { supportOpen, setSupportOpen } = useCrisp();

  const { activeGlobalDeviceId, effectiveDrawerDeviceId, routeDeviceId } =
    useGlobalDeviceContext();
  const globalDeviceId = activeGlobalDeviceId ?? "";
  const { data: devices } = useGetDevices({ orgId });
  const drawerNavigationDeviceId =
    effectiveDrawerDeviceId || routeDeviceId || devices?.[0]?.id || "";
  const traceabilityDeviceId = drawerNavigationDeviceId;
  const { data: todosSummary } = useGetUserTodosSummary(
    orgId,
    globalDeviceId || undefined,
    isAuthenticated && !!orgId
  );

  const hasOutstandingActions = (todosSummary?.totalCount ?? 0) > 0;
  const todosBadgeCount = todosSummary?.totalCount ?? 0;
  const org = getOrgFromUser(user, orgId);
  const { data: changelogIndex } = useGetChangelogIndex();
  const hasUnseenChangelog =
    !!changelogIndex?.[0]?.date &&
    uiStore.lastSeenChangelogDate !== changelogIndex[0].date;
  const isCollapsed = uiStore.isLeftPanelCollapsed;
  const drawerRootClassName = getLeftDrawerRootClassName(hasBanner);
  const isGeneralSectionExpanded = isGeneralExpanded;
  const isMoreSectionExpanded = isMoreExpanded;

  const clickUserMenuIcon = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorUserMenu(event.currentTarget);
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const closeUserMenu = () => {
    setAnchorUserMenu(null);
    setIsUserMenuOpen(false);
  };

  const clickOrgMenuIcon = (event: React.MouseEvent<Element, MouseEvent>) => {
    setAnchorOrgMenu(event.currentTarget);
    setIsOrgMenuOpen(!isOrgMenuOpen);
  };

  const closeOrgMenu = () => {
    setAnchorOrgMenu(null);
    setIsOrgMenuOpen(false);
  };

  const getFullRoute = (route: ROUTES, user: User, orgId: string) => {
    return fillRouteWithSlugs(route, {
      ...(user && { [ROUTE_SLUGS.ORG_ID]: orgId }),
    });
  };

  if (!user || !org) {
    return (
      <LeftDrawerSkeleton hasBanner={hasBanner} isCollapsed={isCollapsed} />
    );
  }

  const navItemHoverSx = {
    marginRight: 0,
    "&:hover": {
      bgcolor: "rgba(0, 0, 0, 0.04)",
    },
  };
  const drawerIconColor = "rgba(0, 0, 0, 0.54)";
  const drawerListItemIconSx = { color: drawerIconColor };
  const drawerSvgIconSx = { fontSize: 20 };
  const hasDrawerNavigationDevice = Boolean(drawerNavigationDeviceId);
  const documentsPath = getFullRoute(ROUTES.DOCUMENTS, user, orgId);
  const documentsPathWithAgentContext = getRouteWithAgentContext(
    documentsPath,
    search
  );
  const roadmapPath = hasDrawerNavigationDevice
    ? fillRouteWithSlugs(ROUTES.DEVICE_OVERVIEW, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
        [ROUTE_SLUGS.DEVICE_ID]: drawerNavigationDeviceId,
      })
    : "";
  const noDeviceRoadmapTooltip = "Create or select a device to use Roadmap";

  return (
    <div
      data-testid="left-drawer-root"
      className={`${drawerRootClassName} flex w-full min-h-0 min-w-0 flex-col pt-[8px] pb-[8px]`}
    >
      <List disablePadding className="shrink-0">
        {/* HEADER */}
        <Tooltip
          title={org.name || "Unknown Org"}
          placement="right"
          disableHoverListener={!isCollapsed}
        >
          <ListItem
            disablePadding
            className="text-none flex w-full items-center gap-1 justify-start text-black"
          >
            <ListItemButton
              onClick={(e: React.MouseEvent<HTMLDivElement>) =>
                clickUserMenuIcon(e)
              }
              className="min-w-0 gap-4 flex-1"
              sx={navItemHoverSx}
            >
              <Box
                className={cn(
                  "w-6 h-6 rounded flex items-center justify-center text-xs font-medium"
                )}
                sx={{
                  bgcolor:
                    (org.id && stringToColorHash(org.id)) ||
                    theme.palette.grey[200],
                  minWidth: "21px",
                }}
              >
                {org.name ? getFirst2Initials(org.name) : "?"}
              </Box>
              {!isCollapsed && (
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <ListItemText
                    primary={org.name || "Unknown Org"}
                    sx={{
                      flex: "1 1 auto",
                      margin: 0,
                      minWidth: 0,
                      "& .MuiListItemText-primary": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                  <ArrowDropDown
                    className="shrink-0"
                    sx={{ color: drawerIconColor, fontSize: 18 }}
                  />
                </div>
              )}
            </ListItemButton>
          </ListItem>
        </Tooltip>

        <UserMenu
          isOpen={isUserMenuOpen}
          close={closeUserMenu}
          anchorUserMenu={anchorUserMenu}
          user={user}
          org={org}
          anchorOrgMenu={anchorOrgMenu}
          isOrgMenuOpen={isOrgMenuOpen}
          closeOrgMenu={closeOrgMenu}
          clickOrgMenuIcon={clickOrgMenuIcon}
        />
      </List>

      <List
        key="general-nav-list"
        data-testid="left-drawer-general-section"
        disablePadding
        className="shrink-0"
      >
        <SidebarSectionHeader
          isCollapsed={isCollapsed}
          isExpanded={isGeneralSectionExpanded}
          onToggle={() => setIsGeneralExpanded((current) => !current)}
          title="General"
        />
        <Collapse in={isGeneralSectionExpanded}>
          <Tooltip
            title="Dashboard"
            placement="right"
            disableHoverListener={!isCollapsed}
          >
            <ListItemButton
              selected={
                getFullRoute(ROUTES.DASHBOARD, user, orgId) === currentPath
              }
              component={Link}
              to={getFullRoute(ROUTES.DASHBOARD, user, orgId)}
              state={{ previous: pathname }}
              className="gap-4"
              sx={navItemHoverSx}
            >
              <ListItemIcon className="min-w-fit" sx={drawerListItemIconSx}>
                <DashboardIcon sx={drawerSvgIconSx} />
              </ListItemIcon>
              <ListItemText primary="Dashboard" sx={{ whiteSpace: "nowrap" }} />
            </ListItemButton>
          </Tooltip>
          <Tooltip
            title="Tasks"
            placement="right"
            disableHoverListener={!isCollapsed}
          >
            <ListItemButton
              selected={getFullRoute(ROUTES.TASKS, user, orgId) === currentPath}
              component={Link}
              to={
                globalDeviceId
                  ? `${getFullRoute(ROUTES.TASKS, user, orgId)}?deviceId=${globalDeviceId}`
                  : getFullRoute(ROUTES.TASKS, user, orgId)
              }
              state={{ previous: pathname }}
              className="gap-4"
              sx={{ ...navItemHoverSx }}
            >
              <ListItemIcon className="min-w-fit" sx={drawerListItemIconSx}>
                <Badge
                  className="ml-[1px]"
                  color="error"
                  invisible={!hasOutstandingActions}
                  badgeContent={todosBadgeCount}
                >
                  <Assignment sx={drawerSvgIconSx} />
                </Badge>
              </ListItemIcon>
              <ListItemText primary={"Tasks"} sx={{ whiteSpace: "nowrap" }} />
            </ListItemButton>
          </Tooltip>
          <Tooltip
            title="Documents"
            placement="right"
            disableHoverListener={!isCollapsed}
          >
            <ListItemButton
              selected={
                getFullRoute(ROUTES.DOCUMENTS, user, orgId) === currentPath
              }
              component={Link}
              to={documentsPathWithAgentContext}
              state={{ previous: pathname }}
              className="gap-4"
              sx={navItemHoverSx}
            >
              <ListItemIcon className="min-w-fit" sx={drawerListItemIconSx}>
                <FolderOpenRoundedIcon sx={drawerSvgIconSx} />
              </ListItemIcon>
              <ListItemText primary="Documents" sx={{ whiteSpace: "nowrap" }} />
            </ListItemButton>
          </Tooltip>
          {hasDrawerNavigationDevice ? (
            <Tooltip
              title="Roadmap"
              placement="right"
              disableHoverListener={!isCollapsed}
            >
              <ListItemButton
                key="roadmap"
                data-testid="roadmap-nav-button"
                selected={roadmapPath === currentPath}
                component={Link}
                to={roadmapPath}
                state={{ previous: pathname }}
                className="gap-4"
                sx={navItemHoverSx}
              >
                <ListItemIcon className="min-w-fit" sx={drawerListItemIconSx}>
                  <Route sx={drawerSvgIconSx} />
                </ListItemIcon>
                <ListItemText primary="Roadmap" sx={{ whiteSpace: "nowrap" }} />
              </ListItemButton>
            </Tooltip>
          ) : (
            <Tooltip title={noDeviceRoadmapTooltip} placement="right">
              <span className="block" data-testid="roadmap-nav-tooltip-target">
                <ListItemButton
                  key="roadmap"
                  data-testid="roadmap-nav-button"
                  disabled
                  className="gap-4"
                  sx={navItemHoverSx}
                >
                  <ListItemIcon className="min-w-fit" sx={drawerListItemIconSx}>
                    <Route sx={drawerSvgIconSx} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Roadmap"
                    sx={{ whiteSpace: "nowrap" }}
                  />
                </ListItemButton>
              </span>
            </Tooltip>
          )}
          <Tooltip
            title="Trainings"
            placement="right"
            disableHoverListener={!isCollapsed}
          >
            <ListItemButton
              selected={
                getFullRoute(ROUTES.TRAININGS, user, orgId) === currentPath
              }
              component={Link}
              to={getFullRoute(ROUTES.TRAININGS, user, orgId)}
              state={{ previous: pathname }}
              className="gap-4"
              sx={navItemHoverSx}
            >
              <ListItemIcon className="min-w-fit" sx={drawerListItemIconSx}>
                <School sx={drawerSvgIconSx} />
              </ListItemIcon>
              <ListItemText primary="Trainings" sx={{ whiteSpace: "nowrap" }} />
            </ListItemButton>
          </Tooltip>
          {isQualityEventsMenuEnabled && (
            <Tooltip
              title="Quality Event Management"
              placement="right"
              disableHoverListener={!isCollapsed}
            >
              <ListItemButton
                selected={pathname.startsWith(`/${orgId}/quality-events`)}
                component={Link}
                to={`/${orgId}/quality-events`}
                state={{ previous: pathname }}
                className="gap-4"
                sx={navItemHoverSx}
              >
                <ListItemIcon className="min-w-fit" sx={drawerListItemIconSx}>
                  <Widgets sx={drawerSvgIconSx} />
                </ListItemIcon>
                <ListItemText
                  primary="Quality Events"
                  sx={{ whiteSpace: "nowrap" }}
                />
              </ListItemButton>
            </Tooltip>
          )}
          {isChangeManagementMenuEnabled && (
            <Tooltip
              title="Change management"
              placement="right"
              disableHoverListener={!isCollapsed}
            >
              <ListItemButton
                selected={pathname.startsWith(
                  `/${orgId}/change-management-plans`
                )}
                component={Link}
                to={`/${orgId}/change-management-plans`}
                state={{ previous: pathname }}
                className="gap-4"
                sx={navItemHoverSx}
              >
                <ListItemIcon className="min-w-fit" sx={drawerListItemIconSx}>
                  <Difference sx={drawerSvgIconSx} />
                </ListItemIcon>
                <ListItemText
                  primary="Change management"
                  sx={{ whiteSpace: "nowrap" }}
                />
              </ListItemButton>
            </Tooltip>
          )}
          <Tooltip
            title="Traceability"
            placement="right"
            disableHoverListener={!isCollapsed}
          >
            <ListItemButton
              selected={
                !!traceabilityDeviceId &&
                fillRouteWithSlugs(ROUTES.TRACEABILITY_MATRIX, {
                  [ROUTE_SLUGS.ORG_ID]: orgId,
                  [ROUTE_SLUGS.DEVICE_ID]: traceabilityDeviceId,
                }) === currentPath
              }
              component={Link}
              to={
                traceabilityDeviceId
                  ? fillRouteWithSlugs(ROUTES.TRACEABILITY_MATRIX, {
                      [ROUTE_SLUGS.ORG_ID]: orgId,
                      [ROUTE_SLUGS.DEVICE_ID]: traceabilityDeviceId,
                    })
                  : getFullRoute(ROUTES.MANAGE_ORGANIZATION, user, orgId)
              }
              state={{ previous: pathname }}
              className="gap-4"
              sx={navItemHoverSx}
            >
              <ListItemIcon className="min-w-fit" sx={drawerListItemIconSx}>
                <AccountTree sx={drawerSvgIconSx} />
              </ListItemIcon>
              <ListItemText
                primary="Traceability"
                sx={{ whiteSpace: "nowrap" }}
              />
            </ListItemButton>
          </Tooltip>
        </Collapse>
      </List>

      <div className="shrink-0" data-testid="left-drawer-lower-navigation">
        <DeviceNavbar
          isCollapsed={isCollapsed}
          deviceIdOverride={drawerNavigationDeviceId}
        />
      </div>

      <List
        data-testid="left-drawer-more-section"
        disablePadding
        className="mt-auto shrink-0"
      >
        <SidebarSectionHeader
          isCollapsed={isCollapsed}
          isExpanded={isMoreSectionExpanded}
          onToggle={() => setIsMoreExpanded((current) => !current)}
          title="More"
        />
        <Collapse in={isMoreSectionExpanded}>
          <Tooltip
            title="Documentation"
            placement="right"
            disableHoverListener={!isCollapsed}
          >
            <ListItemButton
              component={Link}
              to={"https://docs.formly.ai"}
              className="gap-4"
              sx={{ ...navItemHoverSx }}
              target="_blank"
            >
              <ListItemIcon className="min-w-fit" sx={drawerListItemIconSx}>
                <MenuBook sx={drawerSvgIconSx} />
              </ListItemIcon>
              <ListItemText primary="Documentation" />
            </ListItemButton>
          </Tooltip>
          <Tooltip
            title="Changelog"
            placement="right"
            disableHoverListener={!isCollapsed}
          >
            <ListItemButton
              onClick={() => uiStore.setIsChangelogModalOpen(true)}
              className="gap-4"
              sx={{ ...navItemHoverSx }}
            >
              <ListItemIcon className="min-w-fit" sx={drawerListItemIconSx}>
                <Badge
                  variant="dot"
                  color="primary"
                  invisible={!hasUnseenChangelog}
                >
                  <Campaign sx={drawerSvgIconSx} />
                </Badge>
              </ListItemIcon>
              <ListItemText primary="Changelog" />
            </ListItemButton>
          </Tooltip>
          <Tooltip
            title="Support Chat"
            placement="right"
            disableHoverListener={!isCollapsed}
          >
            <ListItemButton
              onClick={() => setSupportOpen(!supportOpen)}
              className="gap-4"
              sx={{ ...navItemHoverSx }}
            >
              <ListItemIcon className="min-w-fit" sx={drawerListItemIconSx}>
                <Chat sx={drawerSvgIconSx} />
              </ListItemIcon>
              <ListItemText
                primary="Support Chat"
                sx={{ whiteSpace: "nowrap" }}
              />
            </ListItemButton>
          </Tooltip>
        </Collapse>
      </List>
    </div>
  );
});
